import { useEffect, useState } from "react";
import axios from "axios";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import { auth } from "../firebase";

export default function AdminDonationEditor() {
    const [donation, setDonation] = useState([]);
    const [sponsors, setSponsors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openIndex, setOpenIndex] = useState(null);
    const [openSponsorIndex, setOpenSponsorIndex] = useState(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState({});
    const [sponsorUploading, setSponsorUploading] = useState({});

    useEffect(() => {
        const fetchDonation = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/public/donation", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setDonation(res.data.content || []);
            } catch (error) {
                console.error("Error fetching donation:", error);
                toast.error("Error fetching items");
            } finally {
                setLoading(false);
            }
        };
        
        fetchDonation();
    }, []);

    useEffect(() => {
        const fetchSponsors = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/public/sponsors", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const sponsorData = res.data.content || [];
                // Handle both array and object format
                if (Array.isArray(sponsorData)) {
                    setSponsors({ logos: sponsorData, rows: 3 });
                } else {
                    setSponsors(sponsorData.logos ? sponsorData : { logos: sponsorData, rows: 3 });
                }
            } catch (error) {
                console.error("Error fetching sponsors:", error);
            }
        };
        
        fetchSponsors();
    }, []);


    const handleChange = (index, field, value) => {
        const updated = [...donation];
        updated[index][field] = value;
        setDonation(updated);
    };

    const moveDonation = (index, direction) => {
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= donation.length) return;

        const updated = [...donation];
        const temp = updated[index];
        updated[index] = updated[newIndex];
        updated[newIndex] = temp;

        setDonation(updated);
        setOpenIndex(newIndex);
    };

    const saveDonation = async () => {
        setSaving(true);
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/donation", { 
                content: donation
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.success("Donation saved!");
        } catch {
            toast.error("Failed to save donation");
        } finally {
            setSaving(false);
        }
    };

    const saveSponsors = async () => {
        setSaving(true);
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/sponsors", { 
                content: sponsors
            }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.success("Sponsors saved!");
        } catch {
            toast.error("Failed to save sponsors");
        } finally {
            setSaving(false);
        }
    };

    const paymentMethods = [
        { id: "visa", label: "Visa", icon: "fab fa-cc-visa" },
        { id: "mastercard", label: "Mastercard", icon: "fab fa-cc-mastercard" },
        { id: "amex", label: "American Express", icon: "fab fa-cc-amex" },
        { id: "discover", label: "Discover", icon: "fab fa-cc-discover" },
        { id: "paypal", label: "PayPal", icon: "fab fa-cc-paypal" }
    ];

    const addDonation = () => {
        const newDonation = {
            icon: "",
            label: "",
            header: "",
            description: "",
            image: "",
            enableButton: false,
            buttonLabel: "",
            buttonLink: "",
            buttonIcon: "",
            buttonColor: "primary",
            acceptedPayments: [],
            finePrint: "",
            enableAdditionalSection: false,
            sectionType: "general", // "faq" or "general"
            faqItems: [],
            generalHeader: "",
            generalContent: "",
            generalAsBullets: false
        };
        setDonation([newDonation, ...donation]);
        setOpenIndex(0);
    };

    const deleteDonation = async (index) => {
        const donationSection = donation[index];
        const ok = await confirmSwal({
            title: `Delete donation section "${donationSection.header || donationSection.label || "(No Header)"}"?`,
            text: "Once deleted it cannot be restored.",
            icon: "warning",
            confirmText: "Delete",
            confirmColor: "#d33",
        });
        if (!ok) return;

        const updated = donation.filter((_, i) => i !== index);
        setDonation(updated);
        setOpenIndex(null);

        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/donation", { content: updated }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.info("Donation deleted");
        } catch {
            toast.error("Failed to save after deletion");
        }
    };

    const toggleOpen = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const handleImageUpload = async (index, file) => {
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only JPG, PNG, and HEIC files are allowed');
            return;
        }

        // Validate file size (10MB)
        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        setUploading({ ...uploading, [index]: true });

        try {
            const token = await auth.currentUser.getIdToken();
            const formData = new FormData();
            formData.append('file', file);
            formData.append('subfolder', 'donation');

            const response = await axios.post('/api/upload-image', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                handleChange(index, 'image', response.data.url);
                toast.success('Image uploaded successfully!');
            }
        } catch (error) {
            console.error('Error uploading image:', error);
            toast.error(error.response?.data?.detail || 'Failed to upload image');
        } finally {
            setUploading({ ...uploading, [index]: false });
        }
    };

    const addFaqItem = (index) => {
        const updated = [...donation];
        if (!updated[index].faqItems) {
            updated[index].faqItems = [];
        }
        updated[index].faqItems.push({ question: "", answer: "" });
        setDonation(updated);
    };

    const removeFaqItem = (donationIndex, faqIndex) => {
        const updated = [...donation];
        updated[donationIndex].faqItems = updated[donationIndex].faqItems.filter((_, i) => i !== faqIndex);
        setDonation(updated);
    };

    const handleFaqChange = (donationIndex, faqIndex, field, value) => {
        const updated = [...donation];
        updated[donationIndex].faqItems[faqIndex][field] = value;
        setDonation(updated);
    };

    const togglePaymentMethod = (index, methodId) => {
        const updated = [...donation];
        if (!updated[index].acceptedPayments) {
            updated[index].acceptedPayments = [];
        }
        
        const payments = updated[index].acceptedPayments;
        const methodIndex = payments.indexOf(methodId);
        
        if (methodIndex > -1) {
            // Remove if already exists
            payments.splice(methodIndex, 1);
        } else {
            // Add if doesn't exist
            payments.push(methodId);
        }
        
        setDonation(updated);
    };

    const addSponsor = () => {
        const newSponsors = { 
            ...sponsors, 
            logos: [...(sponsors.logos || []), { name: "", logo: "", url: "" }]
        };
        setSponsors(newSponsors);
        // Open the newly added sponsor (last one in the array)
        setTimeout(() => setOpenSponsorIndex(newSponsors.logos.length - 1), 0);
    };

    const moveSponsor = (index, direction) => {
        const newIndex = index + direction;
        const logos = sponsors.logos || [];
        if (newIndex < 0 || newIndex >= logos.length) return;

        const updated = [...logos];
        const temp = updated[index];
        updated[index] = updated[newIndex];
        updated[newIndex] = temp;

        setSponsors({ ...sponsors, logos: updated });
        setOpenSponsorIndex(newIndex);
    };

    const removeSponsor = async (index) => {
        const logos = sponsors.logos || [];
        const sponsor = logos[index];
        const ok = await confirmSwal({
            title: `Delete sponsor "${sponsor.name || "(No Name)"}"?`,
            text: "Once deleted it cannot be restored.",
            icon: "warning",
            confirmText: "Delete",
            confirmColor: "#d33",
        });
        if (!ok) return;

        const updated = logos.filter((_, i) => i !== index);
        const newSponsors = { ...sponsors, logos: updated };
        setSponsors(newSponsors);
        setOpenSponsorIndex(null);

        try {
            const token = await auth.currentUser.getIdToken();
            await axios.put("/api/public/sponsors", { content: newSponsors }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            toast.info("Sponsor deleted");
        } catch {
            toast.error("Failed to save after deletion");
        }
    };

    const toggleSponsorOpen = (index) => {
        setOpenSponsorIndex(openSponsorIndex === index ? null : index);
    };

    const handleSponsorChange = (index, field, value) => {
        const updated = [...(sponsors.logos || [])];
        updated[index][field] = value;
        setSponsors({ ...sponsors, logos: updated });
    };

    const handleSponsorImageUpload = async (index, file) => {
        if (!file) return;

        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/heic', 'image/svg+xml'];
        if (!allowedTypes.includes(file.type)) {
            toast.error('Only JPG, PNG, SVG, and HEIC files are allowed');
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toast.error('File size must be less than 10MB');
            return;
        }

        setSponsorUploading({ ...sponsorUploading, [index]: true });

        try {
            const token = await auth.currentUser.getIdToken();
            const formData = new FormData();
            formData.append('file', file);
            formData.append('subfolder', 'sponsors');

            const response = await axios.post('/api/upload-image', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                },
            });

            if (response.data.success) {
                handleSponsorChange(index, 'logo', response.data.url);
                toast.success('Logo uploaded successfully!');
            }
        } catch (error) {
            console.error('Error uploading logo:', error);
            toast.error(error.response?.data?.detail || 'Failed to upload logo');
        } finally {
            setSponsorUploading({ ...sponsorUploading, [index]: false });
        }
    };

    if (loading) return <p>Loading donation sections...</p>;

    return (
        <div>
            <h4>Donation Sections</h4>
            <p className="text-body-secondary">Donation sections are displayed on the <em>Donation</em> page.</p>

            <button className="btn btn-admin mb-3" onClick={addDonation}>+ Add Donation Section</button>

            <div className="accordion" id="projectsAccordion">
                {donation.map((donationSection, index) => (
                    <div className="accordion-item" key={index}>
                        <h2 className="accordion-header" id={`heading-${index}`}>
                            <button
                                className={`accordion-button ${openIndex === index ? "" : "collapsed"}`}
                                type="button"
                                onClick={() => toggleOpen(index)}
                            >
                                <strong>{donationSection.label || "(No Label)"}</strong>
                            </button>
                        </h2>
                        <div className={`accordion-collapse collapse ${openIndex === index ? "show" : ""}`}>
                            <div className="accordion-body">
                                <div className="mb-2">
                                    <label className="form-label">
                                        Bootstrap Icon <small className="text-body-secondary">(e.g., "bi-heart") - </small>
                                        <a href="https://icons.getbootstrap.com/" target="_blank" rel="noopener noreferrer" className="small-link">
                                            <small>Browse icons</small>
                                        </a>
                                    </label>
                                    <input 
                                        className="form-control" 
                                        value={donationSection.icon || ""} 
                                        onChange={(e) => handleChange(index, "icon", e.target.value)}
                                        placeholder="bi-heart"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Label</label>
                                    <input 
                                        className="form-control" 
                                        value={donationSection.label || ""} 
                                        onChange={(e) => handleChange(index, "label", e.target.value)} 
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Header</label>
                                    <input 
                                        className="form-control" 
                                        value={donationSection.header || ""} 
                                        onChange={(e) => handleChange(index, "header", e.target.value)} 
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Description</label>
                                    <textarea 
                                        className="form-control" 
                                        rows="3"
                                        value={donationSection.description || ""} 
                                        onChange={(e) => handleChange(index, "description", e.target.value)}
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Image <small className="text-body-secondary">(Optional)</small></label>
                                    <input 
                                        className="form-control mb-2" 
                                        value={donationSection.image || ""} 
                                        onChange={(e) => handleChange(index, "image", e.target.value)}
                                        placeholder="Image URL"
                                    />
                                    <div className="input-group">
                                        <input 
                                            type="file" 
                                            className="form-control" 
                                            accept=".jpg,.jpeg,.png,.heic"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) handleImageUpload(index, file);
                                            }}
                                            disabled={uploading[index]}
                                        />
                                        <button 
                                            className="btn btn-outline-secondary" 
                                            type="button"
                                            disabled
                                        >
                                            {uploading[index] ? 'Uploading...' : 'Upload'}
                                        </button>
                                    </div>
                                    {donationSection.image && (
                                        <div className="mt-2">
                                            <img 
                                                src={donationSection.image} 
                                                alt="Preview" 
                                                style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'cover' }}
                                                className="img-thumbnail"
                                            />
                                        </div>
                                    )}
                                </div>

                                <hr className="my-4" />
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">Button Settings (Optional)</h6>
                                    <button 
                                        className={`btn btn-sm ${donationSection.enableButton ? 'btn-admin' : 'btn-admin-outline'}`}
                                        onClick={() => handleChange(index, "enableButton", !donationSection.enableButton)}
                                    >
                                        {donationSection.enableButton ? '- Remove Button' : '+ Add Button'}
                                    </button>
                                </div>
                                
                                {donationSection.enableButton && (
                                    <>
                                        <div className="mb-2">
                                            <label className="form-label">Button Label</label>
                                            <input 
                                                className="form-control" 
                                                value={donationSection.buttonLabel || ""} 
                                                onChange={(e) => handleChange(index, "buttonLabel", e.target.value)}
                                                placeholder="Donate Now"
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label">Button Link</label>
                                            <input 
                                                className="form-control" 
                                                value={donationSection.buttonLink || ""} 
                                                onChange={(e) => handleChange(index, "buttonLink", e.target.value)}
                                                placeholder="https://..."
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label">
                                                Button Icon <small className="text-body-secondary">(e.g., "bi-arrow-right") - </small>
                                                <a href="https://icons.getbootstrap.com/" target="_blank" rel="noopener noreferrer" className="small-link">
                                                    <small>Browse icons</small>
                                                </a>
                                            </label>
                                            <input 
                                                className="form-control" 
                                                value={donationSection.buttonIcon || ""} 
                                                onChange={(e) => handleChange(index, "buttonIcon", e.target.value)}
                                                placeholder="bi-arrow-right"
                                            />
                                        </div>
                                        <div className="mb-2">
                                            <label className="form-label">Button Color</label>
                                            <select 
                                                className="form-select" 
                                                value={donationSection.buttonColor || "primary"} 
                                                onChange={(e) => handleChange(index, "buttonColor", e.target.value)}
                                            >
                                                <option value="primary">Primary</option>
                                                <option value="secondary">Secondary</option>
                                                <option value="success">Success</option>
                                                <option value="danger">Danger</option>
                                                <option value="warning">Warning</option>
                                                <option value="info">Info</option>
                                                <option value="light">Light</option>
                                                <option value="dark">Dark</option>
                                            </select>
                                        </div>

                                        <div className="mt-4 mb-3">
                                            <label className="form-label fw-bold">Accepted Payment Methods <small className="text-body-secondary">(Optional)</small></label>
                                            <small className="text-body-secondary d-block mb-2">Select which payment methods are accepted</small>
                                            
                                            <div className="row">
                                                {paymentMethods.map((method) => (
                                                    <div key={method.id} className="col-md-6 mb-2">
                                                        <div className="form-check">
                                                            <input 
                                                                className="form-check-input" 
                                                                type="checkbox" 
                                                                id={`payment-${index}-${method.id}`}
                                                                checked={(donationSection.acceptedPayments || []).includes(method.id)}
                                                                onChange={() => togglePaymentMethod(index, method.id)}
                                                            />
                                                            <label className="form-check-label" htmlFor={`payment-${index}-${method.id}`}>
                                                                <i className={method.icon}></i> {method.label}
                                                            </label>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="mb-2">
                                            <label className="form-label">Fine Print <small className="text-body-secondary">(Optional)</small></label>
                                            <textarea 
                                                className="form-control" 
                                                rows="2"
                                                value={donationSection.finePrint || ""} 
                                                onChange={(e) => handleChange(index, "finePrint", e.target.value)}
                                                placeholder="Additional information..."
                                            />
                                        </div>
                                    </>
                                )}

                                <hr className="my-4" />
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h6 className="mb-0">Additional Section (Optional)</h6>
                                    <button 
                                        className={`btn btn-sm ${donationSection.enableAdditionalSection ? 'btn-admin' : 'btn-admin-outline'}`}
                                        onClick={() => handleChange(index, "enableAdditionalSection", !donationSection.enableAdditionalSection)}
                                    >
                                        {donationSection.enableAdditionalSection ? '- Remove Section' : '+ Add Section'}
                                    </button>
                                </div>
                                
                                {donationSection.enableAdditionalSection && (
                                    <>
                                        <div className="mb-3">
                                            <label className="form-label">Section Type</label>
                                            <select 
                                                className="form-select" 
                                                value={donationSection.sectionType || "general"} 
                                                onChange={(e) => handleChange(index, "sectionType", e.target.value)}
                                            >
                                                <option value="general">General Section</option>
                                                <option value="faq">FAQ Section</option>
                                            </select>
                                        </div>

                                        {donationSection.sectionType === "faq" ? (
                                            <div>
                                                <h6 className="mb-3">FAQ Items</h6>
                                                {(donationSection.faqItems || []).map((faqItem, faqIndex) => (
                                                    <div key={faqIndex} className="border p-3 mb-3 rounded">
                                                        <div className="mb-2">
                                                            <label className="form-label">Question</label>
                                                            <input 
                                                                className="form-control" 
                                                                value={faqItem.question} 
                                                                onChange={(e) => handleFaqChange(index, faqIndex, "question", e.target.value)}
                                                            />
                                                        </div>
                                                        <div className="mb-2">
                                                            <label className="form-label">Answer</label>
                                                            <textarea 
                                                                className="form-control" 
                                                                rows="3"
                                                                value={faqItem.answer} 
                                                                onChange={(e) => handleFaqChange(index, faqIndex, "answer", e.target.value)}
                                                            />
                                                        </div>
                                                        <button 
                                                            className="btn btn-sm btn-outline-danger" 
                                                            onClick={() => removeFaqItem(index, faqIndex)}
                                                        >
                                                            Remove FAQ
                                                        </button>
                                                    </div>
                                                ))}
                                                <button 
                                                    className="btn btn-sm btn-outline-secondary" 
                                                    onClick={() => addFaqItem(index)}
                                                >
                                                    + Add FAQ Item
                                                </button>
                                            </div>
                                        ) : (
                                            <div>
                                                <div className="mb-3">
                                                    <label className="form-label">General Section Header</label>
                                                    <input 
                                                        className="form-control" 
                                                        value={donationSection.generalHeader || ""} 
                                                        onChange={(e) => handleChange(index, "generalHeader", e.target.value)}
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <label className="form-label">Content</label>
                                                    <textarea 
                                                        className="form-control" 
                                                        rows="6"
                                                        value={donationSection.generalContent || ""} 
                                                        onChange={(e) => handleChange(index, "generalContent", e.target.value)}
                                                        placeholder="Enter content here. If using bullet points, put each item on a new line."
                                                    />
                                                </div>
                                                <div className="form-check mb-3">
                                                    <input 
                                                        className="form-check-input" 
                                                        type="checkbox" 
                                                        id={`bullets-${index}`}
                                                        checked={donationSection.generalAsBullets || false}
                                                        onChange={(e) => handleChange(index, "generalAsBullets", e.target.checked)}
                                                    />
                                                    <label className="form-check-label" htmlFor={`bullets-${index}`}>
                                                        Display as bullet points <small className="text-body-secondary">(each line becomes a bullet)</small>
                                                    </label>
                                                </div>
                                            </div>
                                        )}
                                    </>
                                )}

                                <hr className="my-4" />
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <button
                                            className="btn btn-sm btn-outline-secondary me-2"
                                            onClick={() => moveDonation(index, -1)}
                                            disabled={index === 0}
                                        >
                                            ↑ Move Up
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => moveDonation(index, 1)}
                                            disabled={index === donation.length - 1}
                                        >
                                            ↓ Move Down
                                        </button>
                                    </div>
                                    <button className="btn btn-outline-danger btn-sm" onClick={() => deleteDonation(index)} disabled={saving}>
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <button className="btn btn-admin mt-3" onClick={saveDonation} disabled={saving}>
                {saving ? "Saving..." : "Save"}
            </button>

            {/* Sponsors Section */}
            <hr className="my-4" />
            <h4 className="mb-3">Sponsored By Logos</h4>
            <p className="text-body-secondary">Add sponsor logos that will scroll horizontally at the bottom of the donation page.</p>

            <div className="mb-4">
                <label className="form-label">Number of Logo Rows</label>
                <input
                    type="number"
                    className="form-control"
                    style={{ maxWidth: '150px' }}
                    min="1"
                    max="10"
                    value={sponsors.rows || 3}
                    onChange={(e) => setSponsors({ ...sponsors, rows: parseInt(e.target.value) || 3 })}
                />
                <small className="text-body-secondary">
                    Controls how many rows of logos appear in the scrolling banner (default: 3)
                </small>
            </div>

            <button className="btn btn-admin mb-3" onClick={addSponsor}>+ Add Sponsor Logo</button>

            <div className="accordion" id="sponsorsAccordion">
                {(sponsors.logos || []).map((sponsor, index) => (
                    <div className="accordion-item" key={index}>
                        <h2 className="accordion-header" id={`sponsor-heading-${index}`}>
                            <button
                                className={`accordion-button ${openSponsorIndex === index ? "" : "collapsed"}`}
                                type="button"
                                onClick={() => toggleSponsorOpen(index)}
                            >
                                <strong>{sponsor.name || "(No Name)"}</strong>
                            </button>
                        </h2>
                        <div className={`accordion-collapse collapse ${openSponsorIndex === index ? "show" : ""}`}>
                            <div className="accordion-body">
                                <div className="mb-2">
                                    <label className="form-label">Sponsor Name</label>
                                    <input
                                        className="form-control"
                                        value={sponsor.name || ""}
                                        onChange={(e) => handleSponsorChange(index, "name", e.target.value)}
                                        placeholder="Company Name"
                                    />
                                </div>
                                <div className="mb-2">
                                    <label className="form-label">Logo URL</label>
                                    <input
                                        className="form-control mb-2"
                                        value={sponsor.logo || ""}
                                        onChange={(e) => handleSponsorChange(index, "logo", e.target.value)}
                                        placeholder="https://..."
                                    />
                                    <div className="input-group">
                                        <input
                                            type="file"
                                            className="form-control"
                                            accept=".jpg,.jpeg,.png,.heic,.svg"
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) handleSponsorImageUpload(index, file);
                                            }}
                                            disabled={sponsorUploading[index]}
                                        />
                                        <button
                                            className="btn btn-outline-secondary"
                                            type="button"
                                            disabled
                                        >
                                            {sponsorUploading[index] ? 'Uploading...' : 'Upload'}
                                        </button>
                                    </div>
                                    {sponsor.logo && (
                                        <div className="mt-2">
                                            <img
                                                src={sponsor.logo}
                                                alt={sponsor.name || "Sponsor logo"}
                                                style={{ maxWidth: '200px', maxHeight: '100px', objectFit: 'contain' }}
                                                className="img-thumbnail"
                                            />
                                        </div>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Website URL <small className="text-body-secondary">(Optional)</small></label>
                                    <input
                                        className="form-control"
                                        value={sponsor.url || ""}
                                        onChange={(e) => handleSponsorChange(index, "url", e.target.value)}
                                        placeholder="https://..."
                                    />
                                </div>

                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <div>
                                        <button
                                            className="btn btn-sm btn-outline-secondary me-2"
                                            onClick={() => moveSponsor(index, -1)}
                                            disabled={index === 0}
                                        >
                                            ↑ Move Up
                                        </button>
                                        <button
                                            className="btn btn-sm btn-outline-secondary"
                                            onClick={() => moveSponsor(index, 1)}
                                            disabled={index === (sponsors.logos || []).length - 1}
                                        >
                                            ↓ Move Down
                                        </button>
                                    </div>
                                    <button
                                        className="btn btn-outline-danger btn-sm"
                                        onClick={() => removeSponsor(index)}
                                        disabled={saving}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <button className="btn btn-admin mt-3" onClick={saveSponsors} disabled={saving}>
                {saving ? "Saving..." : "Save"}
            </button>
        </div>
    );
}
